import { useState, useCallback, useRef, useEffect } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { ListingFilter } from '../../domain/models/Filter';
import type { FiltersService, GetFiltersParams } from '../../application/services/FiltersService';
import { getPlpFilterPreviewTags } from '../../utils/buildPlpFilterPreviewTags';

type SelectedOptions = Record<string, string[]>;

function deriveSelectedOptions(filters: ListingFilter[]): SelectedOptions {
  const result: SelectedOptions = {};
  for (const f of filters) {
    const selected = f.items.filter((i) => i.selected).map((i) => i.name);
    if (selected.length > 0) result[f.category] = selected;
  }
  return result;
}

const PREVIEW_DEBOUNCE_MS = 400;
const NAV_DEBOUNCE_MS = 2000;

export interface UsePlpDesktopFiltersParams {
  listingFilters: ListingFilter[];
  filtersResetUrl?: string;
  isLoading?: boolean;
  tags: string;
  filterParams: Omit<GetFiltersParams, 'tags'>;
  service: FiltersService;
  navigate: NavigateFunction;
}

export interface UsePlpDesktopFiltersReturn {
  previewFilters: ListingFilter[];
  selectedOptions: SelectedOptions;
  isPendingNavigation: boolean;
  toggleOption: (category: string, name: string, url: string) => void;
  handleReset: () => void;
}

export function usePlpDesktopFilters({
  listingFilters,
  filtersResetUrl,
  isLoading = false,
  tags,
  filterParams,
  service,
  navigate,
}: UsePlpDesktopFiltersParams): UsePlpDesktopFiltersReturn {
  const [previewFilters, setPreviewFilters] = useState<ListingFilter[]>(listingFilters);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(
    () => deriveSelectedOptions(listingFilters)
  );
  const [isPendingNavigation, setIsPendingNavigation] = useState(false);

  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedOptionsRef = useRef<SelectedOptions>(deriveSelectedOptions(listingFilters));
  const baseFilterUrlRef = useRef<string | null>(null);
  const filtersResetUrlRef = useRef(filtersResetUrl);
  filtersResetUrlRef.current = filtersResetUrl;

  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (isLoading || navTimerRef.current) return;
    setPreviewFilters(listingFilters);
    const synced = deriveSelectedOptions(listingFilters);
    setSelectedOptions(synced);
    selectedOptionsRef.current = synced;
    baseFilterUrlRef.current = null;
    setIsPendingNavigation(false);
  }, [listingFilters, isLoading]);

  const schedulePreviewUpdate = useCallback(
    (filterItemUrl?: string | null) => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = setTimeout(() => {
        const previewTags = getPlpFilterPreviewTags(filterItemUrl, tags);
        service
          .getFilters({ ...filterParams, tags: previewTags, is_for_filter_change: true })
          .then((result) => {
            setPreviewFilters(result.filters);
            if (result.filtersResetUrl) {
              filtersResetUrlRef.current = result.filtersResetUrl;
            }
          })
          .catch(() => {
            // silently keep current preview filters on error
          });
      }, PREVIEW_DEBOUNCE_MS);
    },
    [service, tags, filterParams]
  );

  const scheduleNavigation = useCallback(() => {
    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    setIsPendingNavigation(true);
    navTimerRef.current = setTimeout(() => {
      navTimerRef.current = null;
      const navUrl = baseFilterUrlRef.current;
      baseFilterUrlRef.current = null;
      if (!navUrl) {
        const resetUrl = filtersResetUrlRef.current;
        if (resetUrl) navigate(resetUrl);
      } else {
        navigate(navUrl);
      }
    }, NAV_DEBOUNCE_MS);
  }, [navigate]);

  const toggleOption = useCallback(
    (category: string, name: string, url: string) => {
      const current = selectedOptions[category] ?? [];
      const isSelected = current.includes(name);
      let next: SelectedOptions;
      if (isSelected) {
        const filtered = current.filter((n) => n !== name);
        if (filtered.length === 0) {
          const { [category]: _removed, ...rest } = selectedOptions;
          next = rest;
        } else {
          next = { ...selectedOptions, [category]: filtered };
        }
      } else {
        next = { ...selectedOptions, [category]: [...current, name] };
      }
      setSelectedOptions(next);
      selectedOptionsRef.current = next;
      if (url) {
        baseFilterUrlRef.current = url;
      }
      schedulePreviewUpdate(url);
      scheduleNavigation();
    },
    [selectedOptions, schedulePreviewUpdate, scheduleNavigation]
  );

  const handleReset = useCallback(() => {
    if (navTimerRef.current) {
      clearTimeout(navTimerRef.current);
      navTimerRef.current = null;
    }
    if (previewDebounceRef.current) {
      clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = null;
    }
    selectedOptionsRef.current = {};
    baseFilterUrlRef.current = null;
    setSelectedOptions({});
    setPreviewFilters(listingFilters);
    const resetUrl = filtersResetUrlRef.current;
    if (resetUrl) {
      setIsPendingNavigation(true);
      navigate(resetUrl);
    } else {
      setIsPendingNavigation(false);
    }
  }, [navigate, listingFilters]);

  return {
    previewFilters,
    selectedOptions,
    isPendingNavigation,
    toggleOption,
    handleReset,
  };
}
